import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private router: Router) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const skipGlobalErrorPage = request.headers.get('x-skip-error-page') === 'true';
        const isSubdomainUnavailableFlowActive =
          window.sessionStorage.getItem('suppressGlobalErrorToast') === 'true' ||
          this.router.url.startsWith('/page-not-found?error=subdomain-unavailable') ||
          window.location.pathname.startsWith('/page-not-found');

        if (skipGlobalErrorPage) {
          return throwError(() => error);
        }

        // Once subdomain-unavailable flow is active, do not replace it with backend-disconnected page.
        if (isSubdomainUnavailableFlowActive) {
          return throwError(() => error);
        }

        if (this.isSubdomainUnavailableError(error)) {
          const currentPath = window.location.pathname || '';
          const currentRoute = this.router.url || '';
          const isErrorPage = currentPath.startsWith('/page-not-found') || currentRoute.startsWith('/page-not-found');

          // Avoid repeated redirects and keep caller-level error handling quiet.
          window.sessionStorage.setItem('suppressGlobalErrorToast', 'true');

          if (!isErrorPage) {
            this.router.navigate(['/page-not-found'], {
              queryParams: {
                error: 'subdomain-unavailable'
              },
              state: {
                fromUrl: currentPath || currentRoute
              }
            });
          }

          return throwError(() => error);
        }

        // Check if the error is a backend connection error
        if (this.isBackendConnectionError(error)) {
          const currentPath = window.location.pathname || '';
          const currentRoute = this.router.url || '';
          const isErrorPage = currentPath.startsWith('/page-not-found') || currentRoute.startsWith('/page-not-found');
          const storedHomeRoute = window.sessionStorage.getItem('errorHomeRoute') || '';
          const isPortalRoute = currentPath.includes('/portal') || currentRoute.includes('/portal');
          const homeRoute = isErrorPage && storedHomeRoute
            ? storedHomeRoute
            : (isPortalRoute ? '/portal' : '/');

          // Keep fallback for page-not-found component when query params are missing.
          window.sessionStorage.setItem('errorHomeRoute', homeRoute);

          // Do not keep redirecting while already on error page.
          if (isErrorPage) {
            return throwError(() => error);
          }

          // Navigate to page-not-found with a query parameter to indicate backend error
          this.router.navigate(['/page-not-found'], {
            queryParams: {
              error: 'backend-disconnected',
              home: homeRoute
            },
            state: {
              fromUrl: currentPath || currentRoute,
              homeRoute
            }
          });
          // Return empty error to prevent further error handling
          return throwError(() => error);
        }
        
        // For other errors, just pass them through
        return throwError(() => error);
      })
    );
  }

  private isBackendConnectionError(error: HttpErrorResponse): boolean {
    // Check for network errors (status 0 means the request didn't reach the server)
    // Also check for common server errors that indicate backend is down
    return (
      error.status === 0 || // Network error or CORS issue
      error.status === 502 || // Bad Gateway
      error.status === 503 || // Service Unavailable
      error.status === 504 // Gateway Timeout
    );
  }

  private isSubdomainUnavailableError(error: HttpErrorResponse): boolean {
    if (!error) {
      return false;
    }

    const statusMatches = [400, 401, 403, 404, 422].includes(error.status);
    const errorText = this.collectErrorText(error.error).toLowerCase();

    if (!statusMatches || !errorText) {
      return false;
    }

    const hasSubdomainKeyword = errorText.includes('subdomain');
    const hasUnavailableKeyword =
      errorText.includes('not exist') ||
      errorText.includes('does not exist') ||
      errorText.includes("doesn't exist") ||
      errorText.includes('not found') ||
      errorText.includes('invalid') ||
      errorText.includes('cannot be accessed') ||
      errorText.includes('unavailable');

    return hasSubdomainKeyword && hasUnavailableKeyword;
  }

  private collectErrorText(input: any): string {
    if (!input) {
      return '';
    }

    if (typeof input === 'string') {
      return input;
    }

    if (Array.isArray(input)) {
      return input.map(item => this.collectErrorText(item)).join(' ');
    }

    if (typeof input === 'object') {
      return Object.keys(input)
        .map(key => this.collectErrorText(input[key]))
        .join(' ');
    }

    return '';
  }
}
